import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";

/** Render a Markdown-ish body (basic ## H2, **bold**, paragraphs). */
function renderBody(md = "") {
  const lines = String(md).split(/\r?\n/);
  const out = [];
  let key = 0;
  let bullets = null;
  const flushBullets = () => {
    if (bullets) {
      out.push(
        <ul key={`u-${key++}`} className="list-disc pl-6 space-y-1 my-3 text-slate-700">
          {bullets.map((b, i) => <li key={i} dangerouslySetInnerHTML={{ __html: inline(b) }} />)}
        </ul>
      );
      bullets = null;
    }
  };
  const inline = (s) =>
    s
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.+?)\*/g, "<em>$1</em>")
      .replace(/`([^`]+)`/g, "<code class='bg-slate-100 px-1 rounded text-sm'>$1</code>");

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (/^\s*$/.test(line)) { flushBullets(); continue; }
    if (/^##\s+/.test(line)) {
      flushBullets();
      out.push(<h2 key={key++} className="font-heading text-2xl font-bold text-[var(--brand-accent)] mt-6 mb-2">{line.replace(/^##\s+/, "")}</h2>);
    } else if (/^#\s+/.test(line)) {
      flushBullets();
      out.push(<h1 key={key++} className="font-heading text-3xl font-bold text-[var(--brand-accent)] mt-6 mb-3">{line.replace(/^#\s+/, "")}</h1>);
    } else if (/^[-*]\s+/.test(line)) {
      bullets = bullets || [];
      bullets.push(line.replace(/^[-*]\s+/, ""));
    } else {
      flushBullets();
      out.push(<p key={key++} className="text-slate-700 leading-relaxed my-3" dangerouslySetInnerHTML={{ __html: inline(line) }} />);
    }
  }
  flushBullets();
  return out;
}

export default function SitePage({ slug: slugProp }) {
  const params = useParams();
  const slug = slugProp || params.slug;
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/site/pages/${slug}`);
        if (alive) setPage(data);
      } catch {
        if (alive) setPage(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  return (
    <div className="max-w-3xl mx-auto px-6 py-12" data-testid={`site-page-${slug}`}>
      <Button variant="outline" onClick={() => navigate("/")} className="rounded-full mb-6">
        <ArrowLeft className="w-4 h-4 mr-1" /> Back to home
      </Button>
      <Card className="p-8">
        {loading ? (
          <div className="text-slate-400">Loading…</div>
        ) : !page ? (
          <div className="text-center py-10">
            <div className="font-heading text-2xl font-bold text-[var(--brand-accent)]">Page not found</div>
            <div className="text-slate-500 mt-2">The page <span className="font-mono">{slug}</span> doesn&apos;t exist yet.</div>
          </div>
        ) : (
          <>
            <h1 className="font-heading text-4xl font-bold text-[var(--brand-accent)]" data-testid="site-page-title">
              {page.title}
            </h1>
            <div className="text-xs text-slate-400 mt-1">
              Last updated {new Date(page.updated_at).toLocaleDateString("en-GB")}
            </div>
            <div className="mt-6" data-testid="site-page-body">{renderBody(page.body || "")}</div>
          </>
        )}
      </Card>
    </div>
  );
}
